import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";

const formatDateTime = (raw) => {
  if (!raw) return "—";
  const d = new Date(raw);
  if (!Number.isFinite(d.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
};

const normalize = (v) => String(v || "").trim().toLowerCase();

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const buildExcelFileName = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `keycloak_recent_users_${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}_${pad(d.getHours())}${pad(d.getMinutes())}.xls`;
};

const SORT_COLUMNS = [
  { key: "username", label: "Username", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "lastName", label: "Last name", type: "text" },
  { key: "firstName", label: "First name", type: "text" },
  { key: "createdAt", label: "Creation date", type: "date" },
  { key: "enabled", label: "Status", type: "boolean" },
];

// We want the Keycloak users recently added, not only the filtered "active-users" response.
// Production Nginx usually proxies backend through /api, so the /api routes are tried first.
// The old active-users endpoint can legitimately return [] because it is narrower; therefore
// /keycloak/users is preferred and a successful empty response does not stop the fallback chain.
const KEYCLOAK_RECENT_USERS_ENDPOINTS = [
  // Current production Nginx proxies /keycloak/ directly to Node.
  { url: "/keycloak/users", mode: "users" },
  { url: "/keycloak/active-users", mode: "active" },
  // Keep /api variants only as compatibility fallbacks for other environments.
  { url: "/api/keycloak/users", mode: "users" },
  { url: "/api/keycloak/active-users", mode: "active" },
];

const normalizeKeycloakUser = (u = {}) => {
  const createdTimestamp = u.createdTimestamp || u.created_timestamp || null;
  const createdAt =
    u.createdAt ||
    u.created_at ||
    (createdTimestamp ? new Date(Number(createdTimestamp)).toISOString() : null);

  return {
    id: u.id || u.userId || u.sub || u.username || u.email || "",
    username: u.username || u.preferred_username || "",
    email: u.email || "",
    firstName: u.firstName || u.first_name || "",
    lastName: u.lastName || u.last_name || "",
    enabled: u.enabled !== false,
    emailVerified: u.emailVerified === true || u.email_verified === true,
    createdTimestamp: createdTimestamp ? Number(createdTimestamp) : null,
    createdAt,
    requiredActions: Array.isArray(u.requiredActions) ? u.requiredActions : [],
  };
};

const pickUsersArray = (data) => {
  if (Array.isArray(data)) return data;

  const candidates = [
    data?.users,
    data?.activeUsers,
    data?.active_users,
    data?.items,
    data?.results,
    data?.data,
    data?.data?.users,
    data?.data?.items,
    data?.data?.results,
  ];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) return candidate;
  }

  return [];
};

const extractUsers = (data) => {
  const seen = new Set();
  return pickUsersArray(data)
    .map(normalizeKeycloakUser)
    .filter((u) => {
      const key = normalize(u.id || u.username || u.email);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((a, b) => {
      const at = new Date(a?.createdAt || 0).getTime() || 0;
      const bt = new Date(b?.createdAt || 0).getTime() || 0;
      if (at !== bt) return bt - at;
      return normalize(a?.username).localeCompare(normalize(b?.username));
    });
};

function ActiveUsersPage() {
  const auth = useAuth();
  const token = auth?.token || localStorage.getItem("kc_token") || "";

  const [users, setUsers] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState("");
  const [fetchedAt, setFetchedAt] = useState(null);
  const [endpointUsed, setEndpointUsed] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: "createdAt", direction: "desc" });

  const loadUsers = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      let lastError = null;
      let firstSuccessfulEmpty = null;

      for (const endpoint of KEYCLOAK_RECENT_USERS_ENDPOINTS) {
        try {
          const res = await fetch(endpoint.url, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const data = await res.json().catch(() => ({}));

          if (!res.ok) {
            const message = data?.error || `HTTP ${res.status}`;
            lastError = new Error(`${message} on ${endpoint.url}`);

            // 404 means route/proxy mismatch; try next candidate.
            // 401/403 means auth really failed; do not silently bypass it.
            if ([404, 405, 502, 503, 504].includes(res.status)) continue;
            throw lastError;
          }

          const normalizedUsers = extractUsers(data);

          if (normalizedUsers.length > 0) {
            setUsers(normalizedUsers);
            setFetchedAt(data?.fetched_at || data?.fetchedAt || new Date().toISOString());
            setEndpointUsed(endpoint.url);
            return;
          }

          // Do not stop on an empty /active-users response: it can be narrower than the real
          // Keycloak user list. Keep trying /keycloak/users fallbacks before showing empty.
          if (!firstSuccessfulEmpty) {
            firstSuccessfulEmpty = {
              users: normalizedUsers,
              fetchedAt: data?.fetched_at || data?.fetchedAt || new Date().toISOString(),
              endpointUrl: endpoint.url,
            };
          }
        } catch (err) {
          lastError = err;
          // Network/proxy errors: try next endpoint.
          continue;
        }
      }

      if (firstSuccessfulEmpty) {
        setUsers(firstSuccessfulEmpty.users);
        setFetchedAt(firstSuccessfulEmpty.fetchedAt);
        setEndpointUsed(firstSuccessfulEmpty.endpointUrl);
        return;
      }

      throw lastError || new Error("Unable to load Keycloak users.");
    } catch (err) {
      setError(err?.message || "Unable to load Keycloak users.");
      setUsers([]);
      setEndpointUsed("");
    } finally {
      setHasLoadedOnce(true);
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const q = normalize(query);
    if (!q) return users;
    return users.filter((u) => {
      const blob = [u.username, u.email, u.firstName, u.lastName, u.createdAt]
        .map(normalize)
        .join(" ");
      return blob.includes(q);
    });
  }, [users, query]);

  const getSortValue = useCallback((u, key) => {
    if (key === "createdAt") {
      const ts = new Date(u?.createdAt || 0).getTime();
      return Number.isFinite(ts) ? ts : 0;
    }
    if (key === "enabled") {
      return u?.enabled === false ? 0 : 1;
    }
    return normalize(u?.[key]);
  }, []);

  const sortedUsers = useMemo(() => {
    const rows = Array.isArray(filteredUsers) ? [...filteredUsers] : [];
    const { key, direction } = sortConfig || {};
    const mult = direction === "asc" ? 1 : -1;

    rows.sort((a, b) => {
      const av = getSortValue(a, key);
      const bv = getSortValue(b, key);

      if (typeof av === "number" && typeof bv === "number") {
        if (av !== bv) return (av - bv) * mult;
      } else {
        const cmp = String(av).localeCompare(String(bv), undefined, {
          numeric: true,
          sensitivity: "base",
        });
        if (cmp !== 0) return cmp * mult;
      }

      return normalize(a?.username).localeCompare(normalize(b?.username));
    });

    return rows;
  }, [filteredUsers, getSortValue, sortConfig]);

  const requestSort = useCallback((key) => {
    setSortConfig((prev) => {
      if (prev?.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: key === "createdAt" ? "desc" : "asc" };
    });
  }, []);

  const renderSortIcon = (key) => {
    if (sortConfig?.key !== key) return <i className="bi bi-arrow-down-up wv-sort-muted" />;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-sort-alpha-down wv-sort-active" />
    ) : (
      <i className="bi bi-sort-alpha-up wv-sort-active" />
    );
  };

  const exportToExcel = useCallback(() => {
    const rows = sortedUsers || [];
    if (!rows.length) return;

    const tableRows = rows
      .map((u) => {
        const status = u?.enabled === false ? "Disabled" : "Active";
        return `
          <tr>
            <td>${escapeHtml(u?.username || "")}</td>
            <td>${escapeHtml(u?.email || "")}</td>
            <td>${escapeHtml(u?.lastName || "")}</td>
            <td>${escapeHtml(u?.firstName || "")}</td>
            <td>${escapeHtml(formatDateTime(u?.createdAt))}</td>
            <td>${escapeHtml(status)}</td>
            <td>${escapeHtml(u?.emailVerified ? "Yes" : "No")}</td>
          </tr>`;
      })
      .join("");

    const html = `
      <!doctype html>
      <html>
        <head>
          <meta charset="UTF-8" />
          <style>
            table { border-collapse: collapse; font-family: Arial, sans-serif; }
            th { background: #111827; color: #ffffff; font-weight: 700; }
            th, td { border: 1px solid #d1d5db; padding: 8px 10px; white-space: nowrap; }
          </style>
        </head>
        <body>
          <table>
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Last name</th>
                <th>First name</th>
                <th>Creation date</th>
                <th>Status</th>
                <th>Email verified</th>
              </tr>
            </thead>
            <tbody>${tableRows}</tbody>
          </table>
        </body>
      </html>`;

    const blob = new Blob(["\ufeff", html], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = buildExcelFileName();
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [sortedUsers]);

  const showInitialLoader = loading && !hasLoadedOnce;

  return (
    <div className="wv-active-users-page">
      <style>{`
        .wv-active-users-page{
          padding: 96px 32px 42px 104px;
          min-height: 100vh;
          background: #fafafa;
          color: #111827;
        }
        .wv-active-users-header{
          display:flex;
          align-items:flex-start;
          justify-content:space-between;
          gap:18px;
          margin-bottom:18px;
        }
        .wv-active-users-title h1{
          font-size:34px;
          font-weight:800;
          margin:0 0 6px;
          letter-spacing:-.04em;
        }
        .wv-active-users-title p{
          margin:0;
          color:#6b7280;
          font-size:15px;
        }
        .wv-active-users-card{
          background:#fff;
          border:1px solid rgba(15,23,42,.08);
          border-radius:22px;
          box-shadow:0 18px 45px rgba(15,23,42,.06);
          overflow:hidden;
          position:relative;
        }
        .wv-active-users-toolbar{
          display:flex;
          align-items:center;
          gap:12px;
          padding:18px;
          border-bottom:1px solid rgba(15,23,42,.08);
          flex-wrap:wrap;
        }
        .wv-active-users-search{
          min-width:min(420px, 100%);
          flex:1 1 420px;
          display:flex;
          align-items:center;
          gap:10px;
          background:#f3f4f6;
          border:1px solid rgba(15,23,42,.06);
          border-radius:999px;
          padding:0 14px;
          height:46px;
        }
        .wv-active-users-search input{
          border:none;
          outline:none;
          background:transparent;
          width:100%;
          height:100%;
          font-size:14px;
        }
        .wv-active-users-refresh,
        .wv-active-users-export{
          border:none;
          height:46px;
          border-radius:999px;
          padding:0 18px;
          font-weight:800;
          display:inline-flex;
          align-items:center;
          gap:8px;
          white-space:nowrap;
        }
        .wv-active-users-refresh{
          background:#111827;
          color:#fff;
        }
        .wv-active-users-export{
          background:#ecfdf5;
          color:#047857;
          border:1px solid rgba(4,120,87,.14);
        }
        .wv-active-users-refresh:disabled,
        .wv-active-users-export:disabled{
          opacity:.55;
          cursor:not-allowed;
        }
        .wv-active-users-meta{
          font-size:13px;
          color:#6b7280;
          margin-left:auto;
          white-space:nowrap;
        }
        .wv-active-users-source{
          width:100%;
          color:#94a3b8;
          font-size:12px;
          padding-left:4px;
        }
        .wv-active-users-table-wrap{ overflow:auto; }
        .wv-active-users-table{
          width:100%;
          border-collapse:separate;
          border-spacing:0;
          min-width:960px;
        }
        .wv-active-users-table th{
          text-align:left;
          padding:0;
          font-size:13px;
          color:#374151;
          background:#fbfbfb;
          border-bottom:1px solid rgba(15,23,42,.08);
          white-space:nowrap;
        }
        .wv-sort-button{
          width:100%;
          border:none;
          background:transparent;
          color:inherit;
          padding:15px 18px;
          display:inline-flex;
          align-items:center;
          justify-content:space-between;
          gap:10px;
          font:inherit;
          font-weight:800;
          text-align:left;
          cursor:pointer;
        }
        .wv-sort-button:hover{ background:#f3f4f6; }
        .wv-sort-muted{ color:#9ca3af; font-size:14px; }
        .wv-sort-active{ color:#111827; font-size:15px; }
        .wv-active-users-table td{
          padding:15px 18px;
          border-bottom:1px solid rgba(15,23,42,.07);
          vertical-align:middle;
          font-size:14px;
        }
        .wv-active-users-table tr:hover td{ background:#fafafa; }
        .wv-user-name{
          color:#0066cc;
          font-weight:700;
          display:flex;
          align-items:center;
          gap:10px;
        }
        .wv-status-pill{
          display:inline-flex;
          align-items:center;
          gap:6px;
          border-radius:999px;
          padding:5px 10px;
          font-size:12px;
          font-weight:800;
          background:#ecfdf5;
          color:#047857;
          border:1px solid rgba(4,120,87,.14);
        }
        .wv-status-pill.is-disabled{
          background:#fef2f2;
          color:#b91c1c;
          border-color:#fee2e2;
        }
        .wv-email-warning{ color:#dc2626; margin-right:8px; }
        .wv-active-users-empty,
        .wv-active-users-error{
          padding:32px;
          text-align:center;
          color:#6b7280;
        }
        .wv-active-users-error{
          color:#b91c1c;
          background:#fef2f2;
          border:1px solid #fee2e2;
          margin:18px;
          border-radius:16px;
        }
        .wv-active-users-loader{
          min-height:360px;
          display:flex;
          flex-direction:column;
          align-items:center;
          justify-content:center;
          gap:14px;
          color:#6b7280;
          text-align:center;
          padding:32px;
        }
        .wv-loader-ring{
          width:48px;
          height:48px;
          border-radius:999px;
          border:4px solid #e5e7eb;
          border-top-color:#111827;
          animation:wvSpin .85s linear infinite;
        }
        .wv-loader-title{ font-weight:800; color:#111827; font-size:16px; }
        .wv-loader-subtitle{ font-size:13px; max-width:360px; }
        @keyframes wvSpin{ to{ transform:rotate(360deg); } }
        @media (max-width: 760px){
          .wv-active-users-page{ padding:88px 14px 32px 94px; }
          .wv-active-users-title h1{ font-size:26px; }
          .wv-active-users-header{ flex-direction:column; }
          .wv-active-users-meta{ margin-left:0; width:100%; }
          .wv-active-users-refresh,
          .wv-active-users-export{ width:100%; justify-content:center; }
        }
      `}</style>

      <div className="wv-active-users-header">
        <div className="wv-active-users-title">
          <h1>Recently added Keycloak users</h1>
          <p>Latest Keycloak users in the current realm, restricted to tmpadm.</p>
        </div>
      </div>

      <div className="wv-active-users-card">
        <div className="wv-active-users-toolbar">
          <div className="wv-active-users-search" role="search">
            <i className="bi bi-search" aria-hidden="true" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search user, email, first name, last name…"
              aria-label="Search users"
              disabled={showInitialLoader}
            />
          </div>

          <button
            type="button"
            className="wv-active-users-refresh"
            onClick={loadUsers}
            disabled={loading}
          >
            <i className={`bi ${loading ? "bi-arrow-repeat" : "bi-arrow-clockwise"}`} />
            {loading ? "Loading…" : "Refresh"}
          </button>

          <button
            type="button"
            className="wv-active-users-export"
            onClick={exportToExcel}
            disabled={loading || sortedUsers.length === 0}
            title="Export current filtered and sorted list to Excel"
          >
            <i className="bi bi-file-earmark-excel" />
            Export Excel
          </button>

          <div className="wv-active-users-meta">
            {sortedUsers.length} / {users.length} users
            {fetchedAt ? ` · refreshed ${formatDateTime(fetchedAt)}` : ""}
          </div>
          {endpointUsed ? <div className="wv-active-users-source">Backend endpoint: {endpointUsed}</div> : null}
        </div>

        {error ? (
          <div className="wv-active-users-error">
            {["active_users_admin_only", "keycloak_recent_users_admin_only"].includes(error)
              ? "Access denied. This screen is available only for tmpadm."
              : error}
          </div>
        ) : null}

        {showInitialLoader ? (
          <div className="wv-active-users-loader" role="status" aria-live="polite">
            <div className="wv-loader-ring" aria-hidden="true" />
            <div className="wv-loader-title">Loading Keycloak users…</div>
            <div className="wv-loader-subtitle">
              Fetching the recently added users from Keycloak. The table will appear as soon as data is received.
            </div>
          </div>
        ) : (
          <div className="wv-active-users-table-wrap">
            <table className="wv-active-users-table">
              <thead>
                <tr>
                  {SORT_COLUMNS.map((col) => (
                    <th key={col.key} scope="col" aria-sort={sortConfig.key === col.key ? sortConfig.direction : "none"}>
                      <button
                        type="button"
                        className="wv-sort-button"
                        onClick={() => requestSort(col.key)}
                        title={`Sort by ${col.label}`}
                      >
                        <span>{col.label}</span>
                        {renderSortIcon(col.key)}
                      </button>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedUsers.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="wv-active-users-empty">
                      No Keycloak users found.
                    </td>
                  </tr>
                ) : (
                  sortedUsers.map((u) => {
                    const isDisabled = u.enabled === false;
                    return (
                      <tr key={u.id || u.username}>
                        <td>
                          <span className="wv-user-name">
                            <i className="bi bi-person-circle" aria-hidden="true" />
                            {u.username || "—"}
                          </span>
                        </td>
                        <td>
                          {!u.emailVerified ? (
                            <i className="bi bi-exclamation-circle-fill wv-email-warning" title="Email not verified" />
                          ) : null}
                          {u.email || "—"}
                        </td>
                        <td>{u.lastName || "—"}</td>
                        <td>{u.firstName || "—"}</td>
                        <td>{formatDateTime(u.createdAt)}</td>
                        <td>
                          <span className={`wv-status-pill ${isDisabled ? "is-disabled" : ""}`}>
                            <i className={`bi ${isDisabled ? "bi-slash-circle-fill" : "bi-check-circle-fill"}`} aria-hidden="true" />
                            {isDisabled ? "Disabled" : "Active"}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default ActiveUsersPage;
